import React, { memo } from 'react';
import { Trash2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const InwardDataTable = memo(({ bundles, onDeleteBundle, documentBarcode, staffId }) => {
  const formatDateTime = (timestamp) => {
    if (!timestamp) return { date: '-', time: '-' };
    const dt = new Date(timestamp);
    // Timestamp is already in IST, display as-is
    const date = dt.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const time = dt.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return { date, time };
  };

  // OPTIMIZATION: Only show the first 50 bundles in the table to reduce DOM nodes
  // User can still see the total count in the header
  const displayBundles = bundles.slice(0, 100);
  const hiddenCount = bundles.length - displayBundles.length;
  
  // Count re-entry bundles
  const reEntryCount = bundles.filter(b => b.is_re_entry).length;

  return (
    <div className="space-y-4" data-testid="inward-data-table">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#1A1F2E]">
          Scanned Bundles
        </h3>
        <div className="flex items-center gap-4 text-sm text-[#5A5F6E]">
          {reEntryCount > 0 && (
            <span className="flex items-center gap-1">
              <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
              Re-Entry: <span className="font-medium text-amber-600">{reEntryCount}</span>
            </span>
          )}
          <span>
            Total: <span className="font-medium text-[#1A1F2E]">{bundles.length}</span>
          </span>
        </div>
      </div>

      {bundles.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5DFD0] rounded-lg p-12 text-center">
          <p className="text-[#5A5F6E]">No bundle barcodes added.</p>
          <p className="text-[#8A8F9E] text-sm mt-1">Scan bundle QR codes to add entries</p>
        </div>
      ) : (
        <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    SL No
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Doc No
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Bundle Barcode
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Status
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Date
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Time
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide">
                    Staff ID
                  </TableHead>
                  <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase tracking-wide text-center">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayBundles.map((bundle, index) => {
                  const { date, time } = formatDateTime(bundle.timestamp);
                  return (
                    <TableRow
                      key={bundle.bundle_barcode}
                      className={`border-b border-[#E5DFD0] hover:bg-[#FFFBF5] transition-colors duration-150 ${bundle.is_re_entry ? 'bg-amber-50/50' : ''}`}
                      data-testid={`bundle-row-${index}`}
                    >
                      <TableCell className="font-medium text-[#1A1F2E]">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#5A5F6E]">
                        {documentBarcode || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#1A1F2E] font-medium">
                        {bundle.bundle_barcode}
                      </TableCell>
                      <TableCell>
                        {bundle.is_re_entry ? (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Re-Entry
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                            New
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">
                        {date}
                      </TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">
                        {time}
                      </TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">
                        {staffId || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          type="button"
                          onClick={() => onDeleteBundle(index)}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-[#F5D5D8] hover:text-[#B8374A]"
                          data-testid={`delete-bundle-${index}-button`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {hiddenCount > 0 && (
                  <TableRow className="bg-gray-50">
                    <TableCell colSpan={8} className="text-center text-sm text-gray-500 py-3">
                      ... and {hiddenCount} more bundles (showing first 100 for performance)
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
});

InwardDataTable.displayName = 'InwardDataTable';

export default InwardDataTable;